import { shapeFormResult, type FieldSpec, type FieldValue } from "./formFields.ts";

/** One row of the plan card's list: a line naming it and a line under it. */
export interface PlanItem {
	name: string;
	detail: string;
}

const DIGITS = "零一二三四五六七八九";

/** 1 → 一, 10 → 十, 12 → 十二, 21 → 二十一: how an item is counted aloud, up to ninety-nine. */
export function chineseOrdinal(n: number): string {
	if (n < 10) return DIGITS[n]!;
	const tens = Math.floor(n / 10);
	const ones = n % 10;
	return `${tens === 1 ? "" : DIGITS[tens]}十${ones ? DIGITS[ones] : ""}`;
}

export function parseItems(raw: unknown): { ok: true; items: PlanItem[] } | { ok: false; error: string } {
	if (!Array.isArray(raw)) return { ok: false, error: "计划条目不是列表" };
	const items: PlanItem[] = [];
	for (const entry of raw) {
		const e = entry as Partial<PlanItem> | null;
		if (typeof e?.name !== "string" || typeof e.detail !== "string") return { ok: false, error: "计划中有条目无法读取" };
		items.push({ name: e.name, detail: e.detail });
	}
	return { ok: true, items };
}

/** The list with one item moved a place up (-1) or down (1); unchanged at either end. */
export function moveItem(items: PlanItem[], index: number, delta: -1 | 1): PlanItem[] {
	const target = index + delta;
	if (target < 0 || target >= items.length) return items;
	const next = [...items];
	[next[index], next[target]] = [next[target]!, next[index]!];
	return next;
}

export function removeItem(items: PlanItem[], index: number): PlanItem[] {
	return items.filter((_, i) => i !== index);
}

/** What the card posts: the title, the direction by field name, and the rows the user kept. */
export function shapePlanResult(title: string, fields: FieldSpec[], state: Record<string, FieldValue>, items: PlanItem[]) {
	return {
		title: title.trim(),
		direction: shapeFormResult(fields, state) as Record<string, string>,
		items: items.map((i) => ({ name: i.name.trim(), detail: i.detail.trim() })).filter((i) => i.name !== ""),
	};
}
